import {
  type CSSProperties,
  type ChangeEvent,
  type FormEvent,
  useEffect,
  useRef,
  useState,
} from 'react';

import type { Editor as TiptapEditor } from '@tiptap/core';

import {
  ButtonIcon,
  FormInput,
  IconArrowUp,
} from '@spotify-internal/encore-web';
import { Popover } from '@spotify-internal/encore-web/next';

import type { NewFeedback } from './reviewFeedback';

import styles from './ReviewSelectionMenu.module.css';

type SelectionBounds = {
  height: number;
  left: number;
  top: number;
  width: number;
};

type ActiveFeedbackSelection = Omit<NewFeedback, 'feedback'> & {
  bounds: SelectionBounds;
};

const ACTIVE_REVIEW_SELECTION_ID = 'active-review-selection';

const removeActiveReviewSelection = (editor: TiptapEditor): void => {
  if (editor.isDestroyed) {
    return;
  }

  editor.view.dispatch(
    editor.state.tr.setMeta('removePendingComment', ACTIVE_REVIEW_SELECTION_ID),
  );
};

const getSelectionBounds = (
  editor: TiptapEditor,
  from: number,
  to: number,
): SelectionBounds => {
  const start = editor.view.domAtPos(from);
  const end = editor.view.domAtPos(to);
  const range = document.createRange();
  range.setStart(start.node, start.offset);
  range.setEnd(end.node, end.offset);
  const bounds = range.getBoundingClientRect();

  return {
    height: bounds.height,
    left: bounds.left,
    top: bounds.top,
    width: bounds.width,
  };
};

type ReviewSelectionMenuProps = {
  editor: TiptapEditor;
  onAdd: (feedback: NewFeedback) => void;
};

export const ReviewSelectionMenu = ({
  editor,
  onAdd,
}: ReviewSelectionMenuProps) => {
  const [feedback, setFeedback] = useState('');
  const [activeSelection, setActiveSelection] =
    useState<ActiveFeedbackSelection | null>(null);
  const selectionAnchorRef = useRef<HTMLSpanElement>(null);
  const selectionInputRef = useRef<HTMLElement>(null);
  const positionFrameRef = useRef<number | null>(null);
  const selectionFrameRef = useRef<number | null>(null);

  const clearActiveSelection = (): void => {
    removeActiveReviewSelection(editor);
    setActiveSelection(null);
  };

  useEffect(() => {
    const updateActiveSelection = (): void => {
      const domSelection = window.getSelection();

      if (
        !domSelection ||
        domSelection.isCollapsed ||
        !domSelection.anchorNode ||
        !domSelection.focusNode ||
        !editor.view.dom.contains(domSelection.anchorNode) ||
        !editor.view.dom.contains(domSelection.focusNode)
      ) {
        removeActiveReviewSelection(editor);
        setActiveSelection(null);
        return;
      }

      const anchor = editor.view.posAtDOM(
        domSelection.anchorNode,
        domSelection.anchorOffset,
      );
      const head = editor.view.posAtDOM(
        domSelection.focusNode,
        domSelection.focusOffset,
      );
      const from = Math.min(anchor, head);
      const to = Math.max(anchor, head);
      const quote = editor.state.doc.textBetween(from, to, ' ').trim();

      if (!quote) {
        removeActiveReviewSelection(editor);
        setActiveSelection(null);
        return;
      }

      removeActiveReviewSelection(editor);
      editor.view.dispatch(
        editor.state.tr.setMeta('addPendingComment', {
          id: ACTIVE_REVIEW_SELECTION_ID,
          from,
          to,
        }),
      );
      setActiveSelection({
        from,
        to,
        quote,
        bounds: getSelectionBounds(editor, from, to),
      });
    };
    const scheduleActiveSelectionUpdate = (): void => {
      if (selectionFrameRef.current !== null) {
        window.cancelAnimationFrame(selectionFrameRef.current);
      }

      selectionFrameRef.current = window.requestAnimationFrame(() => {
        selectionFrameRef.current = null;
        updateActiveSelection();
      });
    };
    let detachSelectionListeners: (() => void) | undefined;
    const attachSelectionListeners = (): void => {
      const editorElement = editor.view.dom;

      editorElement.addEventListener(
        'pointerup',
        scheduleActiveSelectionUpdate,
      );
      editorElement.addEventListener('keyup', scheduleActiveSelectionUpdate);
      detachSelectionListeners = () => {
        editorElement.removeEventListener(
          'pointerup',
          scheduleActiveSelectionUpdate,
        );
        editorElement.removeEventListener(
          'keyup',
          scheduleActiveSelectionUpdate,
        );
      };
    };

    if (editor.isInitialized) {
      attachSelectionListeners();
    } else {
      editor.on('create', attachSelectionListeners);
    }

    return () => {
      editor.off('create', attachSelectionListeners);
      detachSelectionListeners?.();
      if (selectionFrameRef.current !== null) {
        window.cancelAnimationFrame(selectionFrameRef.current);
        selectionFrameRef.current = null;
      }
      removeActiveReviewSelection(editor);
    };
  }, [editor]);

  const activeSelectionFrom = activeSelection?.from;
  const activeSelectionTo = activeSelection?.to;

  useEffect(() => {
    if (activeSelectionFrom === undefined || activeSelectionTo === undefined) {
      return undefined;
    }

    const focusFrame = window.requestAnimationFrame(() => {
      selectionInputRef.current?.focus();
    });

    return () => window.cancelAnimationFrame(focusFrame);
  }, [activeSelectionFrom, activeSelectionTo]);

  useEffect(() => {
    if (activeSelectionFrom === undefined || activeSelectionTo === undefined) {
      return undefined;
    }

    const updateSelectionPosition = (): void => {
      const bounds = getSelectionBounds(
        editor,
        activeSelectionFrom,
        activeSelectionTo,
      );
      setActiveSelection(currentSelection =>
        currentSelection ? { ...currentSelection, bounds } : null,
      );
      positionFrameRef.current = window.requestAnimationFrame(() => {
        positionFrameRef.current = null;
        window.dispatchEvent(new Event('resize'));
      });
    };
    const scheduleSelectionPositionUpdate = (event: Event): void => {
      if (event.type === 'resize' && !event.isTrusted) {
        return;
      }

      if (positionFrameRef.current !== null) {
        return;
      }

      positionFrameRef.current = window.requestAnimationFrame(
        updateSelectionPosition,
      );
    };

    window.addEventListener('scroll', scheduleSelectionPositionUpdate, true);
    window.addEventListener('resize', scheduleSelectionPositionUpdate);

    return () => {
      window.removeEventListener(
        'scroll',
        scheduleSelectionPositionUpdate,
        true,
      );
      window.removeEventListener('resize', scheduleSelectionPositionUpdate);
      if (positionFrameRef.current !== null) {
        window.cancelAnimationFrame(positionFrameRef.current);
        positionFrameRef.current = null;
      }
    };
  }, [activeSelectionFrom, activeSelectionTo, editor]);

  const addFeedback = (): void => {
    const trimmedFeedback = feedback.trim();
    const selection = activeSelection;

    if (!trimmedFeedback || !selection) {
      return;
    }

    clearActiveSelection();
    onAdd({ ...selection, feedback: trimmedFeedback });
    setFeedback('');
    editor.commands.setTextSelection(selection.to);
  };

  if (!activeSelection) {
    return null;
  }

  const selectionAnchorStyle: CSSProperties = {
    height: activeSelection.bounds.height,
    left: activeSelection.bounds.left,
    top: activeSelection.bounds.top,
    width: activeSelection.bounds.width,
  };

  return (
    <>
      <span
        ref={selectionAnchorRef}
        aria-hidden
        className={styles.selectionAnchor}
        style={selectionAnchorStyle}
      />
      <Popover
        isOpen
        hasNoArrow
        placement="bottom"
        triggerRef={selectionAnchorRef}
        width="min(30rem, calc(100vw - 2 * var(--encore-spacing-base)))"
        className={styles.selectionPopover}
        onOpenChange={isOpen => {
          if (!isOpen) {
            clearActiveSelection();
            setFeedback('');
          }
        }}
      >
        <form
          className={styles.selectionFeedback}
          onSubmit={(event: FormEvent<HTMLFormElement>) => {
            event.preventDefault();
            addFeedback();
          }}
         data-track-source-anchor="676af8709a">
          <FormInput
            ref={selectionInputRef}
            aria-label="Feedback on selection"
            value={feedback}
            size="small"
            placeholder="Add a comment"
            className={styles.selectionInput}
            onChange={(event: ChangeEvent<HTMLInputElement>) =>
              setFeedback(event.target.value)
            }
           data-track-source-anchor="e271343c3d"/>
          <ButtonIcon
            type="submit"
            variant="primary"
            size="small"
            aria-label="Add feedback"
            disabled={!feedback.trim()}
          >
            <IconArrowUp size="xsmall" aria-hidden />
          </ButtonIcon>
        </form>
      </Popover>
    </>
  );
};
